import CursorPaginatedAPI from "./CursorPaginatedAPI";
import { store } from "@/store";
import { storageRoute } from "../routes";
import { isAPIErrorStatus } from "@/services/apiErrors";
import { newIdempotencyKey } from "@/composables/globalMethods";

const encodeStoragePath = (value) => {
    const bytes = new TextEncoder().encode(value);
    return btoa(String.fromCharCode(...bytes))
        .replace(/\+/g, "-")
        .replace(/\//g, "_");
};

export default class StorageAPI extends CursorPaginatedAPI {
    constructor(emitterInstance) {
        super(emitterInstance, storageRoute, "file", 1000);
    }

    async Create(formData, folder, uploadProgressCallback) {
        if (folder) {
            formData.append("folder", folder);
        }
        const result = await this._request("post", this._url(), formData, {
            timeout: 0, // large upload on slow mobile networks — no global timeout
            headers: {
                "Content-Type": "multipart/form-data",
                "Customer-ID": store.getters.domain,
                "Idempotency-Key": newIdempotencyKey()
            },
            onUploadProgress: (progressEvent) => {
                if (uploadProgressCallback) {
                    const progressPercentage = Math.round(
                        (progressEvent.loaded / progressEvent.total) * 100
                    );
                    uploadProgressCallback(progressPercentage);
                }
            }
        });
        this._emit("add", result);
        return result;
    }

    async Update(id, formData) {
        const result = await this._request("put", this._url(`/${id}`), formData, {
            fullResponse: true,
            timeout: 0, // large upload on slow mobile networks — no global timeout
            headers: {
                "Content-Type": "multipart/form-data",
                "Customer-ID": store.getters.domain
            }
        });
        this._emit("update", result.data.data);
        return result;
    }

    async GetByID(id, folder, downloadProgressCallback) {
        const encodedId = encodeStoragePath(folder ? `${folder}/${id}` : id);
        return this._request("get", this._url(`/${encodedId}`), null, {
            rawData: true,
            timeout: 0, // large file/image download — no global timeout
            responseType: "blob",
            onDownloadProgress: (progressEvent) => {
                if (downloadProgressCallback) {
                    const progressPercentage = Math.round(
                        (progressEvent.loaded / progressEvent.total) * 100
                    );
                    downloadProgressCallback(progressPercentage);
                }
            }
        });
    }

    async GetSignedURL(id, folder) {
        const encodedId = encodeStoragePath(folder ? `${folder}/${id}` : id);
        return this._request("get", this._url(`/${encodedId}/signed-url`), null, {
            headers: { "Customer-ID": store.getters.domain }
        });
    }

    async Delete(id, callback) {
        const encodedId = encodeStoragePath(id);
        try {
            const result = await this._request("delete", this._url(`/${encodedId}`));
            this._emit("delete", encodedId);
            if (callback) callback(null, true);
            return result;
        } catch (err) {
            if (isAPIErrorStatus(err, 404)) {
                this._emit("delete", encodedId);
                if (callback) callback(null, true);
                return null;
            }
            if (callback) callback(err, false);
            throw err;
        }
    }
}
